import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab, Box, Divider } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useNavigate, useParams } from 'react-router-dom';
import OrderTimeline from 'pages/order-details/order-timeline/order-timeline.component';
import { getOrderDetailsTemplate } from 'apollo/templates/order';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useRefreshOrder } from 'hooks/useRefreshOrder';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { getExpectedErrorFeedback } from 'components/shared/denthub-error-boundary/expected-graphql-errors';
import { permissions } from 'utils/permission';
import { isSupplier } from 'utils/client-settings';
import { isRealCurrency } from 'utils/currency';
import { currentUserVar } from 'apollo/reactive-variables/user';
import LabInternalOrderDetails from 'pages/order-details/lab-internal-order-details/lab-internal-order-details.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import WorkflowLog from './workflow-log/workflow-log.componenet';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { AvailableVersion, Order } from 'models/order';
import MainOrderView from './main-order-view/main-order-view.component';
import { Helmet } from 'react-helmet-async';
import Loader from 'components/shared/loader/loader.component';
import { socketIo } from 'context/socket';
import { CurrencyContext } from 'context/currency';
import DeliveryNoteSnackbar from 'components/shared/delivery-note-snackbar/delivery-note-snackbar.component';
import { OrderDetailsContext } from './order-details-context';
import OrderUnavailable from './order-unavailable.component';

const OrderDetails: FC = () => {
    const { t: translate } = useTranslation(['orders', 'deviations']);
    const { orderId, tab } = useParams<{ orderId: string; tab: string }>();
    const [tabState, setTabState] = useState(tab || 'main-view');
    const [version, setVersion] = useState(0);
    const [maxVersion, setMaxVersion] = useState(0);
    // An older version is a snapshot held locally; the live order is read from the cache, so
    // an ORDER_CHANGED refresh (DD-3231) or a list refetch re-renders this page in place.
    const [versionedOrder, setVersionedOrder] = useState<Order | null>(null);
    // Empty until the order loads: a placeholder that looked like a real currency would block
    // the first seed below. The provider only renders once the order is loaded anyway.
    const [orderCurrency, setOrderCurrency] = useState('');
    const [openToaster, setOpenToaster] = useState(false);
    const [toasterType, setToasterType] = useState<'success' | 'info' | 'warning' | 'error' | undefined>(undefined);
    const [toasterTitle, setToasterTitle] = useState<string | null>(null);
    const [spinner, setSpinner] = useState(false);
    const [deliveryNotePdfUrl, setDeliveryNotePdfUrl] = useState<string | null>(null);
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const navigate = useNavigate();
    const socket = useContext(socketIo.getSocketContext());
    const { setCurrency } = useContext(CurrencyContext);
    const { showBoundary } = useDenthubErrorBoundary();

    const refreshOrder = useRefreshOrder();
    const onError = (error: Error) => showBoundary(error);

    // DD-3791: when the backend refuses the order itself (not found, no access, or an external
    // technician who is not on it), the page says so in place of the order. Anything else is
    // still a crash for the error boundary.
    const {
        data: liveData,
        error: liveError,
        refetch,
    } = useQuery(getOrderDetailsTemplate, {
        variables: { orderId },
        skip: !orderId,
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        onError: (error) => {
            if (!getExpectedErrorFeedback(error)) showBoundary(error);
        },
    });
    const unavailableFeedback = getExpectedErrorFeedback(liveError);

    const [getVersionedOrder] = useLazyQuery(getOrderDetailsTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: ({ order }) => setVersionedOrder(order ?? null),
        onError,
    });

    const liveOrder: Order | undefined = liveData?.order;
    const order: Order = (version > 0 ? versionedOrder : liveOrder) ?? ({} as Order);

    useEffect(() => {
        if (!order._id) return;
        // An order can lack version snapshots (a copied database cut before them):
        // fall back to its own version rather than reducing an empty list.
        const maxVersion = ((order.availableVersions ?? []) as AvailableVersion[]).reduce(
            (max, availableVersion) => Math.max(max, availableVersion.version || 0),
            order.version || 0,
        );
        setMaxVersion(maxVersion);
        setCurrency(order.currency);
        // The constructions grids lift an XXX order's currency from its price data into
        // this context. A refetch (e.g. after Cancel) must not wipe that back to XXX: only
        // a real currency on the order, or a context with nothing real yet, re-seeds it.
        setOrderCurrency((previousCurrency) =>
            isRealCurrency(order.currency) || !isRealCurrency(previousCurrency) ? order.currency : previousCurrency,
        );
    }, [order]);

    const handleVersionChange = (version: number) => {
        setVersion(version);
        if (version > 0) {
            setVersionedOrder(null);
            getVersionedOrder({ variables: { orderId, version } });
        } else {
            refetch();
        }
    };

    const handleTabChange = (_event: unknown, newTabState: string) => {
        if (tabState !== newTabState) {
            navigate(`/order-details/${orderId}/${newTabState}`);
            setTabState(newTabState);
        }
    };

    const showWorkflowTab = () => {
        const isLabWorkflowEnabled = customerSettings.getCustomerSetting(
            currentOrganization.customerSettings,
            CONSTANTS.SETTINGS_NAMES.LAB_WORKFLOW,
        );
        return isSupplier(currentOrganization._id, order.supplier._id) && !!isLabWorkflowEnabled;
    };

    // DD-3689: the internal details belong to the lab doing the work. The lab that placed the
    // order (e.g. the customer on a sub-contractor order) does not get the tab.
    const showLabInternalTab = () =>
        permissions.isLabTechnician(currentUser.type) && isSupplier(currentOrganization._id, order.supplier._id);

    useEffect(() => {
        handleTabChange(undefined, tab || 'main-view');
    }, [tab]);

    // A saved link to the internal tab lands on the order details instead.
    useEffect(() => {
        if (order._id && tabState === 'lab-internal' && !showLabInternalTab()) {
            handleTabChange(undefined, 'main-view');
        }
    }, [order._id, tabState]);

    // Back to the live order on every tab or order change, and re-read it on a tab change as
    // before; the first load is the query itself.
    const [loadedTabKey, setLoadedTabKey] = useState(`${orderId}:${tab}`);
    useEffect(() => {
        setVersion(0);
        setVersionedOrder(null);
        const tabKey = `${orderId}:${tab}`;
        if (tabKey !== loadedTabKey) {
            setLoadedTabKey(tabKey);
            if (orderId) refetch();
        }
    }, [tab, orderId]);

    useEffect(() => {
        if (deliveryNotePdfUrl) {
            // The delivery note is written to the order server-side: re-read it (DD-3231).
            refreshOrder(orderId as string);
        }
    }, [deliveryNotePdfUrl]);

    useEffect(() => {
        const onDeliveryNotesPdf = (data: { deliveryNotesPdfUrl?: string; sessionStr?: string }) => {
            try {
                const storedSessionStr = sessionStorage.getItem(CONSTANTS.SOCKET_SESSION);
                if (data.deliveryNotesPdfUrl && data.sessionStr === storedSessionStr) {
                    setOpenToaster(true);
                    setToasterType('success');
                    setToasterTitle(translate('orders:DELIVERY_NOTE_CREATED'));
                    setSpinner(false);
                    setDeliveryNotePdfUrl(data.deliveryNotesPdfUrl);
                }
            } catch (error) {
                console.error(error);
            }
        };
        socket.on('DELIVERY_NOTES_PDF', onDeliveryNotesPdf);
        return () => {
            // Only this listener: removeAllListeners() detached every socket listener in the
            // app, the order change and session listeners included.
            socket.off('DELIVERY_NOTES_PDF', onDeliveryNotesPdf);
        };
    }, [socket]);

    const handleDeliveryNoteCreation = () => {
        setOpenToaster(true);
        setToasterType('info');
        setToasterTitle(translate('orders:CREATING_DELIVERY_NOTE'));
        setSpinner(true);
        setDeliveryNotePdfUrl(null);
    };

    const handleClose = () => {
        setOpenToaster(false);
        setToasterType(undefined);
        setToasterTitle(null);
        setSpinner(false);
        setDeliveryNotePdfUrl(null);
    };

    return (
        <Fragment>
            <Helmet>
                <title>{`${translate('orders:ORDER')} ${order.orderId || ''}`} | Denthub</title>
            </Helmet>
            {unavailableFeedback && version === 0 ? (
                <OrderUnavailable feedback={unavailableFeedback} />
            ) : order?._id ? (
                <OrderDetailsContext.Provider value={{ order, refetch }}>
                    <CurrencyContext.Provider value={{ currency: orderCurrency, setCurrency: setOrderCurrency }}>
                        <TabContext value={tabState}>
                            <DeliveryNoteSnackbar
                                open={openToaster}
                                type={toasterType}
                                spinner={spinner}
                                signedUrl={deliveryNotePdfUrl}
                                onClose={handleClose}
                                title={toasterTitle as string}
                            />
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <TabList onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                                    <Tab
                                        label={translate('orders:ORDER_DETAILS')}
                                        value="main-view"
                                        data-test="order-details.main-view-tab"
                                    />
                                    {showLabInternalTab() ? (
                                        <Tab
                                            sx={{ display: 'inline-flex', flexDirection: 'row-reverse' }}
                                            icon={
                                                order?.isInternalOrderData ? (
                                                    <DescriptionOutlinedIcon sx={{ marginTop: '4px' }} />
                                                ) : undefined
                                            }
                                            label={translate('orders:LAB_INTERNAL')}
                                            value="lab-internal"
                                            data-test="order-details.lab-internal-tab"
                                        />
                                    ) : undefined}
                                    {!permissions.isExternalTechnician(currentUser.role) ? (
                                        <Tab
                                            label={translate('orders:ORDER_EVENTS')}
                                            value="order-timeline"
                                            data-test="order-details.order-events-tab"
                                            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                                        />
                                    ) : undefined}
                                    {showWorkflowTab() ? (
                                        <Tab
                                            label={translate('orders:WORKFLOW_LOG')}
                                            value="workflow-log"
                                            data-test="order-details.workflow-log-tab"
                                        />
                                    ) : undefined}
                                </TabList>
                            </Box>

                            <TabPanel sx={{ padding: 0 }} value="main-view">
                                {order?._id ? (
                                    <MainOrderView
                                        order={order}
                                        selectedVersion={version}
                                        currentUser={currentUser}
                                        handleVersionChange={handleVersionChange}
                                        maxVersion={maxVersion}
                                        onHandleDeliveryNoteCreation={handleDeliveryNoteCreation}
                                    />
                                ) : undefined}
                            </TabPanel>
                            <TabPanel sx={{ padding: 0 }} value="lab-internal">
                                {order?._id && showLabInternalTab() ? (
                                    <LabInternalOrderDetails order={order} />
                                ) : undefined}
                            </TabPanel>
                            <TabPanel sx={{ padding: 0 }} value="order-timeline">
                                {order?._id ? <OrderTimeline order={order} /> : undefined}
                            </TabPanel>
                            <TabPanel sx={{ padding: 0 }} value="workflow-log">
                                {order?._id ? <WorkflowLog order={order} /> : undefined}
                            </TabPanel>
                        </TabContext>
                    </CurrencyContext.Provider>
                </OrderDetailsContext.Provider>
            ) : (
                <Loader />
            )}
        </Fragment>
    );
};

export default OrderDetails;
